import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { useUpdateShiftBookedChatNotification } from "@src/appV2/FacilityProfile/api/useUpdateShiftBookedChatNotification";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import { useState } from "react";

interface UseShiftBookedChatNotificationParams {
  workplace: WorkplaceProfileResponse | undefined;
  onSettingsChanged: () => void;
}

export function useShiftBookedChatNotification({
  workplace,
  onSettingsChanged,
}: UseShiftBookedChatNotificationParams) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const workplaceId = workplace?.data?.id ?? "";
  const persistedEnabled =
    workplace?.data?.attributes?.isShiftBookedChatNotificationEnabled ?? false;

  const [enabled, setEnabled] = useState(persistedEnabled);

  const { isLoading: isUpdating, mutateAsync: updateShiftBookedChatNotification } =
    useUpdateShiftBookedChatNotification();

  const handleToggle = async (checked: boolean) => {
    setEnabled(checked);
    try {
      await updateShiftBookedChatNotification({
        workplaceId,
        isShiftBookedChatNotificationEnabled: checked,
      });
      showSuccessToast(tu("Shift-booked notifications updated successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update shift-booked notifications."));
      setEnabled(!checked);
    }
  };

  return {
    enabled,
    handleToggle,
    isUpdating,
  };
}
